import {
  SVG_VIEW_H,
  SVG_VIEW_W,
  geometryRectInSvg,
} from "@/lib/dxfTransform";

interface Props {
  widthMm: number;
  heightMm: number;
}

/**
 * Overlay SVG sibling to the analyzer's preview that draws bbox
 * dimension lines (W on top, H on left) like a CAD drawing.
 *
 * Renders at the same `viewBox` (800×600) as the source SVG so it lives
 * in the same coordinate space — both share the parent's pan/zoom CSS
 * transform, so the overlay scales together with the geometry. The
 * overlay is `pointer-events: none` so it never steals clicks from the
 * pan/zoom surface.
 *
 * Phase 2 of the DXF preview design alignment: only bbox dimensions for
 * now (no per-hole Ø, no corner R) — those need SVG parsing and are
 * planned for later phases as needed.
 */
export function DxfDimensionOverlay({ widthMm, heightMm }: Props): JSX.Element | null {
  const rect = geometryRectInSvg(widthMm, heightMm);
  if (!rect) return null;

  // Offsets: dim line is OFFSET away from the geometry, ext lines reach
  // halfway back so they touch the geometry corner.
  const OFFSET = 14;
  const TICK = 4;

  // Top: width dimension
  const topY = rect.y - OFFSET;
  const topLeft = { x: rect.x, y: topY };
  const topRight = { x: rect.x + rect.w, y: topY };

  // Left: height dimension
  const leftX = rect.x - OFFSET;
  const leftTop = { x: leftX, y: rect.y };
  const leftBottom = { x: leftX, y: rect.y + rect.h };

  const widthLabel = widthMm.toFixed(2);
  const heightLabel = heightMm.toFixed(2);

  // Soft slate; matches the "dim" legend swatch in the footer.
  const STROKE = "#94a3b8";
  const TEXT = "#475569";

  return (
    <svg
      viewBox={`0 0 ${SVG_VIEW_W} ${SVG_VIEW_H}`}
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
      data-testid="dxf-dimension-overlay"
    >
      {/* Top width dimension */}
      <g stroke={STROKE} strokeWidth="0.7" fill="none">
        {/* extension lines from corners up to dim line */}
        <line x1={rect.x} y1={rect.y - 2} x2={rect.x} y2={topY - 2} />
        <line x1={rect.x + rect.w} y1={rect.y - 2} x2={rect.x + rect.w} y2={topY - 2} />
        {/* dim line with end ticks */}
        <line x1={topLeft.x} y1={topY} x2={topRight.x} y2={topY} />
        <line x1={topLeft.x} y1={topY - TICK} x2={topLeft.x} y2={topY + TICK} />
        <line x1={topRight.x} y1={topY - TICK} x2={topRight.x} y2={topY + TICK} />
      </g>
      <text
        x={(topLeft.x + topRight.x) / 2}
        y={topY - 4}
        textAnchor="middle"
        fontSize="11"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fill={TEXT}
      >
        {widthLabel}
      </text>

      {/* Left height dimension */}
      <g stroke={STROKE} strokeWidth="0.7" fill="none">
        <line x1={rect.x - 2} y1={rect.y} x2={leftX - 2} y2={rect.y} />
        <line x1={rect.x - 2} y1={rect.y + rect.h} x2={leftX - 2} y2={rect.y + rect.h} />
        <line x1={leftX} y1={leftTop.y} x2={leftX} y2={leftBottom.y} />
        <line x1={leftX - TICK} y1={leftTop.y} x2={leftX + TICK} y2={leftTop.y} />
        <line x1={leftX - TICK} y1={leftBottom.y} x2={leftX + TICK} y2={leftBottom.y} />
      </g>
      <text
        x={leftX - 4}
        y={(leftTop.y + leftBottom.y) / 2}
        textAnchor="middle"
        fontSize="11"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fill={TEXT}
        transform={`rotate(-90 ${leftX - 4} ${(leftTop.y + leftBottom.y) / 2})`}
      >
        {heightLabel}
      </text>
    </svg>
  );
}
